'use client';
/**
 * Mon dossier RH (lot N6) — l'écran du droit PAR LIGNE.
 *
 * « Chacun voit le sien, personne d'autre ; la RH voit tout. » L'écran ne
 * décide rien de cela : il demande UN dossier au moteur, qui le rend ou le
 * refuse. Le sélecteur de personne n'apparaît qu'à qui a la portée large, et
 * s'il apparaissait à tort, le serveur répondrait quand même 403 — c'est là que
 * le droit se joue, pas ici.
 *
 * Ce que cet écran n'affiche pas, il ne l'a jamais reçu : le moteur n'envoie
 * dans ce dossier ni pointages bruts, ni variables de paie, ni pièces de
 * pré-embauche (CIN, photo, RIB). Ces trois-là gardent leurs rubriques
 * réservées et leur consultation journalisée.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { I, TitrePage, card, EmptyState, Skeleton } from '@/components/ui';
import { nomDe } from '@/lib/nom';
import { SignaturePersonne } from '@/components/SignaturePersonne';

type Dossier = {
  sien: boolean;
  portee: 'tout' | 'moi';
  personne: {
    orgId: string; nom: string; fonction: string | null; type: string; statut: string;
    badgeId: string | null;
    dateEntree: string | null; dateSortie: string | null;
    telephone: string | null; responsableOrgId: string | null;
  };
  contrats: {
    documentId: string; contratType: string; dateDebut: string; dateFin: string | null;
    regimeHebdo: number | null; quotite: number | null; joursCongesAnnuels: number | null;
    traitementHeuresSup: string | null; actif: boolean;
  }[];
  conges: {
    annee: number; droit: number; explication: string; incomplet: boolean;
    report: number; pris: number; solde: number;
  };
};

const fmt = (iso?: string | null) => {
  if (!iso) return '—';
  const [a, m, j] = iso.split('-');
  return `${j}/${m}/${a}`;
};

function Ligne({ label, valeur }: { label: string; valeur: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '7px 0', borderBottom: '1px solid var(--gray-100)' }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{valeur ?? '—'}</span>
    </div>
  );
}

export function MonDossier() {
  const { session } = useGed();
  /**
   * Le résultat porte l'identifiant qu'il concerne. C'est ce qui distingue
   * « pas encore chargé » de « chargé et refusé » sans poser d'état
   * synchrone dans l'effet — `react-hooks/set-state-in-effect` est une ERREUR
   * de lint ici, et remettre un drapeau à faux en tête d'effet en est
   * précisément le cas type.
   */
  const [resultat, setResultat] = React.useState<{ orgId: string; dossier: Dossier | null; erreur: string } | null>(null);

  /* TOUJOURS LE SIEN (RQ, 04/10/2026) : le sélecteur qui ouvrait le dossier
     des autres au gestionnaire est retiré — ils sont dans la rubrique
     « Dossiers RH » de la barre. Le profil reste « mon espace ». */
  const orgId = session.key;
  const charge = resultat?.orgId === orgId;
  const dossier = charge ? resultat?.dossier ?? null : null;
  const erreur = charge ? resultat?.erreur ?? '' : '';

  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/rh/dossier/${encodeURIComponent(orgId)}`, { credentials: 'include' })
      .then(async (r) => ({ ok: r.ok, corps: await r.json().catch(() => null) }))
      .then(({ ok, corps }) => {
        if (!vivant) return;
        if (ok && corps?.personne) { setResultat({ orgId, dossier: corps as Dossier, erreur: '' }); return; }
        // Le message du serveur EST le message utile : il nomme ce qui manque
        // (un rôle, une ligne qui n'est pas la sienne). Le remplacer par un
        // « accès refusé » générique enverrait la personne chez
        // l'administrateur sans rien lui apprendre.
        const m = corps?.error?.message || corps?.error;
        setResultat({ orgId, dossier: null, erreur: typeof m === 'string' && m ? m : 'Ce dossier n’a pas pu être lu.' });
      })
      .catch(() => {
        if (vivant) setResultat({ orgId, dossier: null, erreur: 'Ce dossier n’a pas pu être lu.' });
      });
    return () => { vivant = false; };
  }, [orgId]);

  return (
    <section>
      <TitrePage eyebrow={<>Personnel &amp; plannings</>}
        titre={dossier && !dossier.sien ? `Dossier de ${nomDe(dossier.personne)}` : 'Mon dossier RH'}
        sousTitre={<>
          Votre situation administrative telle qu’elle est enregistrée : contrat, ancienneté, solde de
          congés. Une correction se demande au gestionnaire RH — ces éléments ne se modifient pas ici.
        </>} />

      {!charge && <Skeleton />}

      {charge && erreur && (
        <EmptyState titre="Dossier non consultable" indice={erreur} />
      )}

      {charge && dossier && (
        <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginTop: 14 }}>
          <div style={{ ...card, padding: '16px 18px' }}>
            <h2 style={{ fontSize: 14, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <I n="req" s={15} /> Situation
            </h2>
            <Ligne label="Nom" valeur={nomDe(dossier.personne)} />
            <Ligne label="Fonction" valeur={dossier.personne.fonction} />
            <Ligne label="N° de badge" valeur={dossier.personne.badgeId} />
            <Ligne label="Entrée" valeur={fmt(dossier.personne.dateEntree)} />
            {dossier.personne.dateSortie && <Ligne label="Sortie" valeur={fmt(dossier.personne.dateSortie)} />}
            <Ligne label="Statut" valeur={dossier.personne.statut} />
          </div>

          <div style={{ ...card, padding: '16px 18px' }}>
            <h2 style={{ fontSize: 14, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <I n="history" s={15} /> Congés {dossier.conges.annee}
            </h2>
            <Ligne label="Droit annuel" valeur={`${dossier.conges.droit} j`} />
            <Ligne label="Report" valeur={`${dossier.conges.report} j`} />
            <Ligne label="Pris" valeur={`${dossier.conges.pris} j`} />
            <Ligne label="Solde" valeur={<strong>{dossier.conges.solde} j</strong>} />
            {/* L'explication du droit VIENT DU SERVEUR : le calcul (mode fixe,
                mensuel ou à l'ancienneté) est paramétrable, le refaire ici
                l'aurait figé. `incomplet` dit que le calcul manque d'une
                donnée — le taire ferait passer un chiffre partiel pour un
                chiffre sûr. */}
            <p style={{ fontSize: 12.5, color: dossier.conges.incomplet ? 'var(--amber-600)' : 'var(--text-muted)', marginTop: 8 }}>
              {dossier.conges.incomplet ? '⚠ ' : ''}{dossier.conges.explication}
            </p>
          </div>

          <div style={{ ...card, padding: '16px 18px' }}>
            <h2 style={{ fontSize: 14, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <I n="doc" s={15} /> Contrats
            </h2>
            {!dossier.contrats.length ? (
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Aucun contrat enregistré. Sans lui, le module ne peut ni décompter les congés ni
                distinguer les heures supplémentaires.
              </p>
            ) : dossier.contrats.map((c) => (
              <div key={c.documentId} style={{ padding: '6px 0', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {c.contratType} {c.actif ? '' : '(clos)'}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  du {fmt(c.dateDebut)}{c.dateFin ? ` au ${fmt(c.dateFin)}` : ''}
                  {c.regimeHebdo ? ` · ${c.regimeHebdo} h/semaine` : ''}
                  {c.quotite ? ` · quotité ${c.quotite}%` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ma signature — c'est MOI qui la dépose et la modifie (11/10/2026). */}
      {charge && dossier && <div style={{ marginTop: 14 }}><SignaturePersonne orgId={orgId} modifiable={dossier.sien !== false} /></div>}
    </section>
  );
}
